import { useCallback, useLayoutEffect, useMemo, useState, type RefObject } from 'react';

export type PopoverWidthMode = 'trigger' | 'fixed';
export type PopoverOpenUpwardMode = 'always' | 'never' | 'onlyOnMobile';

export interface UsePopoverPositionOptions {
  open: boolean;
  triggerRef: RefObject<HTMLElement | null>;
  /** Approximate popover height in px, used to decide open direction. */
  estimatedHeight: number;
  /** How the popover width is decided. */
  widthMode?: PopoverWidthMode;
  /** Width in px when widthMode is 'fixed'. */
  fixedWidth?: number;
  /** Minimum distance from viewport edge. */
  clampMargin?: number;
  /** Gap between trigger and popover. */
  gap?: number;
  /** When to allow opening upward. */
  openUpwardMode?: PopoverOpenUpwardMode;
  /** Viewport width threshold for 'onlyOnMobile'. */
  mobileBreakpointPx?: number;
  /** Reposition on scroll events (capturing). */
  repositionOnScroll?: boolean;
  /** Reposition on window resize. */
  repositionOnResize?: boolean;
}

export interface PopoverPosition {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  openUpward: boolean;
}

export function usePopoverPosition({
  open,
  triggerRef,
  estimatedHeight,
  widthMode = 'trigger',
  fixedWidth,
  clampMargin = 16,
  gap = 4,
  openUpwardMode = 'always',
  mobileBreakpointPx = 1024,
  repositionOnScroll = true,
  repositionOnResize = true,
}: UsePopoverPositionOptions): PopoverPosition {
  const initialWidth = useMemo(() => {
    if (widthMode === 'fixed') return fixedWidth ?? 0;
    return 0;
  }, [fixedWidth, widthMode]);

  const [position, setPosition] = useState<PopoverPosition>({
    top: 0,
    left: 0,
    width: initialWidth,
    openUpward: false,
  });

  const recalculate = useCallback(() => {
    const triggerEl = triggerRef.current;
    if (!triggerEl) return;

    const rect = triggerEl.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const resolvedWidth = widthMode === 'fixed' ? (fixedWidth ?? rect.width) : rect.width;

    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    const allowOpenUpward =
      openUpwardMode === 'always' ? true : openUpwardMode === 'never' ? false : viewportWidth <= mobileBreakpointPx;

    const shouldOpenUpward = allowOpenUpward && spaceBelow < estimatedHeight + clampMargin && spaceAbove > spaceBelow;

    let left = rect.left;
    if (left + resolvedWidth > viewportWidth - clampMargin) {
      left = viewportWidth - resolvedWidth - clampMargin;
    }
    if (left < clampMargin) left = clampMargin;

    if (shouldOpenUpward) {
      setPosition({
        top: undefined,
        bottom: viewportHeight - rect.top + gap,
        left,
        width: resolvedWidth,
        openUpward: true,
      });
      return;
    }

    setPosition({
      top: rect.bottom + gap,
      bottom: undefined,
      left,
      width: resolvedWidth,
      openUpward: false,
    });
  }, [clampMargin, estimatedHeight, fixedWidth, gap, mobileBreakpointPx, openUpwardMode, triggerRef, widthMode]);

  useLayoutEffect(() => {
    if (!open) return;
    recalculate();
  }, [open, recalculate]);

  useLayoutEffect(() => {
    if (!open) return;

    const handle = () => recalculate();

    if (repositionOnResize) window.addEventListener('resize', handle);
    if (repositionOnScroll) window.addEventListener('scroll', handle, true);

    return () => {
      if (repositionOnResize) window.removeEventListener('resize', handle);
      if (repositionOnScroll) window.removeEventListener('scroll', handle, true);
    };
  }, [open, recalculate, repositionOnResize, repositionOnScroll]);

  return position;
}
